import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import TipSelector from '../components/tip/TipSelector';
import Text from '../components/common/Text';
import Button from '../components/common/Button';
import SectionHeader from '../components/common/SectionHeader';
import HotelLogo from '../components/common/HotelLogo';
import formatEuros from '../utils/formatEuros';
import {
  getFoliosOfReservation, getReservation, sendEvent, setCurrentFolioIds, setCurrentTip,
} from '../redux/reducer/actions';

const TippingPage = () => {
  const { uniqueId, propertyId, selectedItemId } = useParams();
  const navigate = useNavigate();
  const [truncated, setTruncated] = useState(true);
  const reservation = useSelector((state) => state.app.reservation).data;
  const reservationFolios = useSelector((state) => state.app.reservationFolios);
  const currentFolioIds = useSelector((state) => state.app.currentFolioIds).data;
  const { paymentPageUrl } = useSelector((state) => state.app.urls);
  const [tipAmount, setTipAmount] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const total = tipAmount + subtotal;
  const tipPercentage = (100 * tipAmount) / subtotal;
  const dispatch = useDispatch();

  useEffect(() => {
    setCurrentTip({ tip: 0 });
  }, []);

  useEffect(() => {
    if (reservationFolios && currentFolioIds?.length > 0) {
      const folioMap = new Map(reservationFolios.map((folio) => [folio.id, folio.balance.amount]));
      const totalAmount = currentFolioIds.reduce((sum, currentFolioId) => sum + (folioMap.get(currentFolioId) || 0), 0);
      setSubtotal(Math.abs(totalAmount));
    } else {
      (async () => {
        try {
          const reservationData = await dispatch(getReservation({ uniqueId, hotelId: propertyId, roomId: selectedItemId }));
          if (reservationData && reservationData.payload && reservationData?.payload.length > 0) {
            const data = await dispatch(getFoliosOfReservation({ uniqueId, hotelId: propertyId, reservationId: reservationData.payload[0].id }));
            if (data.payload) {
              const validFolios = data.payload.filter((folio) => folio.status === 'Open' && folio.balance.amount !== 0);
              const folios = validFolios.map((folio) => folio.id);
              dispatch(setCurrentFolioIds({ folioIds: folios }));
              const totalAmount = validFolios
                .flatMap((folio) => folio.charges || []) // Flatten charges arrays
                .reduce((sum, charge) => sum + (charge.amount?.grossAmount || 0), 0); // Sum up gross amounts
              setSubtotal(Math.abs(totalAmount));
            }
          }
        } catch (error) {
          console.error('Error fetching reservation data:', error);
        }
      })();
    }
  }, [currentFolioIds, dispatch, propertyId, reservationFolios, selectedItemId, uniqueId]);

  const showMoreButton = (
    <Button
      variant="transparent"
      text="^ show more"
      type="button"
      onClick={() => setTruncated(false)}
    />
  );

  const showLessButton = (
    <Button
      variant="transparent"
      text="⌄ show less"
      type="button"
      onClick={() => setTruncated(true)}
    />
  );

  const iDontTipButton = (
    <Button
      variant="secondary"
      text="I don't tip"
      type="button"
      onClick={() => {
        dispatch(setCurrentTip({ tip: 0 }));
        dispatch(sendEvent({ roomId: reservation.property.code, reservationId: reservation.id, type: 'NO_TIP_SELECTED' }));
        setTruncated(true);
        navigate(paymentPageUrl);
      }}
    />
  );

  const continueButton = (
    <Button
      variant="primary"
      text="Continue"
      type="button"
      onClick={() => {
        dispatch(setCurrentTip({ tip: tipAmount }));
        dispatch(sendEvent({ roomId: reservation.property.code, reservationId: reservation.id, type: `TIP ADDED: ${tipAmount}` }));
        setTruncated(true);
        navigate(paymentPageUrl);
      }}
    />
  );

  const fullParagraphText = 'All tips are equally divided among the entire hotel staff. This way, we can assure that all staff members - independent of their field of work & position - receive a small acknowledgment for helping you to enjoy your stay.';

  const shortParagraphText = 'All tips are equally divided among the entire hotel staff.';

  return (
    <main className="flex min-h-screen flex-col gap-16 py-16">
      <HotelLogo />
      <section className="flex flex-col gap-6">
        <SectionHeader text="Add a tip" />
        <Text>{truncated ? shortParagraphText : fullParagraphText}</Text>
        <div className="ml-auto">
          {truncated ? showMoreButton : showLessButton}
        </div>
      </section>
      <section className="flex flex-col gap-6">
        <div className="flex items-center justify-between gap-4 border-b-2 border-b-pj-gray-800 py-8 text-pj-gray-800">
          <div className="flex flex-col gap-2">
            <span className="">Subtotal</span>
            <span className="text-3xl font-semibold">
              {formatEuros(subtotal)}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <span className="">
              Tip(
              {formatEuros(tipPercentage)}
              %)
            </span>
            <span className="text-3xl font-semibold">
              {formatEuros(tipAmount)}
            </span>
          </div>
        </div>

        <TipSelector tipAmount={tipAmount} setTipAmount={setTipAmount} />

        <div className="flex items-center justify-between gap-4 text-pj-gray-800">
          <div>{tipAmount === 0 ? iDontTipButton : continueButton}</div>
          <div className="flex flex-col gap-2">
            <span className="">Total</span>
            <span className="text-3xl font-semibold">{formatEuros(total)}</span>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TippingPage;
